import React, { useState, useCallback, useMemo } from 'react';
import { Box, Button, Typography } from '@lucid/lego';
import type { QuoteListItem } from '../../../../../api/services/lupp/prospect/dto.prospect';
import type {
  Quote,
  QuoteVinStatus,
  QuoteStatus,
} from '../../../../../api/services/lupp/quotes/dto.quotes';
import type { Theme } from '../utils';
import Table from './Quotes/Table/Table';
import styles from './QuotationsTab.module.scss';

type QuotationsTabProps = {
  theme: Theme;
  prospectId: string;
  onCreateQuotation: () => void;
  quoteList: QuoteListItem[];
  labels: Record<string, string>;
  onQuoteClick: (quoteId: string) => void;
  locale?: string;
};

/**
 * Quotations tab content component
 * Displays a table of quotes with search and pagination
 * @component
 * @param props - Component props
 * @param props.theme - Theme for the component
 * @param props.prospectId - The prospect identifier to fetch quotes for
 * @param props.onCreateQuotation - Handler for create quotation button click
 * @returns JSX.Element
 * @example
 * <QuotationsTab theme="dark" prospectId="123" onCreateQuotation={() => {}} />
 */
const QuotationsTab: React.FC<QuotationsTabProps> = ({
  theme,
  prospectId,
  onCreateQuotation,
  quoteList,
  labels,
  onQuoteClick,
  locale = 'en-US',
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Map QuoteListItem[] to Quote[] for the Table component
  const mappedQuotes = useMemo<Quote[]>(() => {
    return quoteList.map((item) => ({
      quote_identifier: item.quote_identifier,
      model: item.model,
      trim: item.trim,
      vin: item.vin,
      vin_status: item.vin_status as QuoteVinStatus | null,
      total_amount: item.final_subtotal_amount,
      preferredPaymentMethod: item.preferredPaymentMethod,
      expiry_date: item.expiry_date || '',
      status: item.status as QuoteStatus,
    }));
  }, [quoteList]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const renderContent = () => {
    return (
      <Box className={styles.loadingContainer}>
        <Table
          theme={theme === 'dark' ? 'light' : 'dark'}
          data={mappedQuotes}
          labels={labels || {}}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          prospectId={prospectId}
          onQuoteClick={onQuoteClick}
          locale={locale}
        />
      </Box>
    );
  };

  return (
    <Box>
      <Box>
        <Box className={styles.quotesHeader}>
          <Box>
            <Typography variant="headline" size="small" color="white" theme={theme}>
              {labels?.['quotes.quoteListing'] ?? 'Quote Listing'}
            </Typography>
            <Typography variant="body" size="medium" color="white" theme={theme}>
              {labels?.['quotes.viewAndManage'] ?? 'View and manage your quotes'}
            </Typography>
          </Box>
          <Box>
            <Button
              type="primary"
              color="neutral"
              theme={theme}
              dataTestId="create-quotation-button"
              gaComponent="prospect-details"
              label={labels?.['quotes.createNewQuotation'] ?? 'Create New Quotation'}
              handleClick={onCreateQuotation}
            />
          </Box>
        </Box>
        {renderContent()}
      </Box>
    </Box>
  );
};

export default QuotationsTab;
